import { Folder, MoreHorizontal, Star } from "lucide-react";

import { Checkbox } from "@/components/ui";
import type { VaultFolderNode } from "@/types/vault.types";
import { cn } from "@/lib/utils";
import { countFiles } from "@/lib/vault/vaultFileUtils";

interface VaultFolderCardProps {
  folder: VaultFolderNode;
  selected: boolean;
  onOpen: () => void;
  onSelect: () => void;
  accentColor?: string;
}

export function VaultFolderCard({
  folder,
  selected,
  onOpen,
  onSelect,
  accentColor = "var(--primary)",
}: VaultFolderCardProps) {
  const itemCount = countFiles(folder);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className={cn(
        "group relative flex cursor-pointer items-center gap-2.5 rounded-lg border bg-card px-3.5 py-3 transition-colors",
        selected ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40",
      )}
    >
      <Checkbox
        checked={selected}
        onClick={(e) => e.stopPropagation()}
        onCheckedChange={onSelect}
        className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 data-[checked]:opacity-100"
        aria-label={`Select ${folder.name}`}
      />
      <div
        className="flex size-9 shrink-0 items-center justify-center rounded-md"
        style={{ backgroundColor: `${folder.color ?? accentColor}22` }}
      >
        <Folder className="size-4" style={{ color: folder.color ?? accentColor }} aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1 truncate text-sm font-medium text-foreground">
          {folder.name}
          {folder.starred && <Star className="size-3 shrink-0 fill-amber-400 text-amber-400" aria-hidden="true" />}
        </p>
        <p className="mt-0.5 text-[10.5px] text-muted-foreground">
          {itemCount} items · {folder.modified ?? "—"}
        </p>
      </div>
      <button
        type="button"
        className="rounded p-1 text-muted-foreground opacity-0 hover:bg-muted group-hover:opacity-100"
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
        aria-label={`More actions for ${folder.name}`}
      >
        <MoreHorizontal className="size-3.5" />
      </button>
    </div>
  );
}
